// eslint-disable-next-line import/no-named-as-default
import { useMemo } from "react";

import { Tooltip } from "antd";
import { BASE_WELL_LAYER } from "constants/mapLayers.constants";
import { selectBaseLayerResourcePlays, useMapStore } from "stores/map";
import styled from "styled-components";

import { useBaseLayerRefactor } from "components/map/hooks/useBaseLayerRefactor";
import { MapLayerSettings } from "components/project/layers/components/layer-managers/shared/map-layer-settings/MapLayerSettings";

import {
  FlexItem,
  LayerActionsWrapper,
  NodeTitle
} from "../../../../shared/nodes/StyledNodePartials";
import { BaseWellColorPicker } from "./BaseWellColorPicker";

export interface ILayerNode {
  node;
}

export function LayerNode({ node }: ILayerNode) {
  // Get baseLayerResourcePlays from Zustand map store
  const baseLayerResourcePlays = useMapStore(selectBaseLayerResourcePlays);
  const baseLayerRefactor = useBaseLayerRefactor();
  const showBaseWellColorPicker =
    node.key === BASE_WELL_LAYER && baseLayerRefactor === true;

  // Compute display name for Base Layer
  const displayTitle = useMemo(() => {
    if (
      node.title === BASE_WELL_LAYER &&
      baseLayerResourcePlays &&
      baseLayerResourcePlays.length > 0
    ) {
      if (baseLayerResourcePlays.length === 1) {
        return `${node.title} (${baseLayerResourcePlays[0]})`;
      } else {
        return `${node.title} (${baseLayerResourcePlays.length} plays)`;
      }
    }
    return node.title;
  }, [node.title, baseLayerResourcePlays]);

  const content = (
    <NodeTitle>
      <FlexItem>
        {showBaseWellColorPicker && <BaseWellColorPicker />}
        <label>{displayTitle}</label>
      </FlexItem>
      <FlexItem>
        <LayerActionsWrapper>
          <MapLayerSettings
            layerName={node.key}
            zoomSliderProps={{
              layerName: node.key,
              isShapefileLayer: false
            }}
          />
        </LayerActionsWrapper>
      </FlexItem>
    </NodeTitle>
  );

  // If node has tooltip and disabled checkbox, add checkbox-specific tooltip
  if (node.tooltip && node.disableCheckbox) {
    return (
      <NodeWrapper $isDisabled={true}>
        <CheckboxTooltipWrapper>
          <Tooltip title={node.tooltip} placement="top">
            <CheckboxOverlay />
          </Tooltip>
        </CheckboxTooltipWrapper>
        {content}
      </NodeWrapper>
    );
  }

  return content;
}

// Wrapper to position the checkbox tooltip - doesn't affect grid layout
const NodeWrapper = styled.div<{ $isDisabled?: boolean }>`
  position: relative;

  /* Override Ant Design's checkbox background when disabled */
  ${(props) =>
    props.$isDisabled &&
    `
    .ant-tree-checkbox-disabled .ant-tree-checkbox-inner {
      background-color: transparent !important;
      border-color: #d9d9d9 !important;
    }
  `}
`;

// Positioned over the checkbox area to capture hover events
const CheckboxTooltipWrapper = styled.div`
  position: absolute;
  left: -27px; /* Position to cover the checkbox */
  top: 0;
  width: 24px;
  height: 24px;
  z-index: 1;
  pointer-events: auto;
`;

// Invisible overlay that triggers the tooltip
const CheckboxOverlay = styled.div`
  width: 100%;
  height: 100%;
  cursor: not-allowed;
`;
